import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  retryCount: number;
  errorKey: number;
}

/**
 * Catches "Illegal constructor" errors and retries after restoring
 * native constructors that browser extensions may have broken.
 * Resets error state when children change (page navigation).
 */
export class SafeChart extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0, errorKey: 0 };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false, retryCount: 0 });
    }
  }

  componentDidCatch(error: Error) {
    if (error.message.includes("Illegal constructor") && this.state.retryCount < 3) {
      const w = window as unknown as { __native_RO?: typeof ResizeObserver; __native_IO?: typeof IntersectionObserver };
      if (w.__native_RO) window.ResizeObserver = w.__native_RO;
      if (w.__native_IO) window.IntersectionObserver = w.__native_IO;

      setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          retryCount: prev.retryCount + 1,
          errorKey: prev.errorKey + 1,
        }));
      }, 50 * (this.state.retryCount + 1));
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}
